<template>
  <div v-if="visible" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
    <div class="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
      <div class="mt-3 text-center">
        <h3 class="text-lg leading-6 font-medium text-gray-900">เลือกระดับการทำงาน</h3>
        <div class="mt-2 px-7 py-3 space-y-4 text-left">
          
          <!-- Working Level Selection -->
          <div class="border-b pb-4 mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              เลือกระดับการทำงาน:
            </label>
            <div class="space-y-2">
              <label class="inline-flex items-center">
                <input
                  type="radio"
                  v-model="workingLevel"
                  value="cluster"
                  class="form-radio text-blue-600 focus:ring-blue-500"
                />
                <span class="ml-2 text-sm">
                  <span class="font-medium">Cluster หลัก</span>
                  <span class="block text-gray-500 text-xs">ทำงานกับข้อมูลทั้งคลัสเตอร์</span>
                </span>
              </label>
              <label class="inline-flex items-center">
                <input
                  type="radio"
                  v-model="workingLevel"
                  value="specific"
                  class="form-radio text-blue-600 focus:ring-blue-500"
                />
                <span class="ml-2 text-sm">
                  <span class="font-medium">OM/VC เฉพาะเจาะจง</span>
                  <span class="block text-gray-500 text-xs">เลือก Operating Model และ Value Chain ที่ต้องการ</span>
                </span>
              </label>
            </div>
          </div>
          
          <!-- Specific OM/VC Selection (shown only when workingLevel is 'specific') -->
          <div v-if="workingLevel === 'specific'" class="space-y-4">
            <!-- OM Selection -->
            <div>
              <label for="omSelect" class="block text-sm font-medium text-gray-700 mb-1">
                เลือก Operation Model (OM):
              </label>
              <select
                id="omSelect"
                v-model="selectedOmId"
                @change="handleOmChange"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option disabled value="">-- กรุณาเลือก OM --</option>
                <option v-for="om in omData" :key="om._id" :value="om._id">
                  {{ om.name }} (ปี {{ om.year }})
                </option>
              </select>
            </div>

            <!-- VC Selection -->
            <div v-if="selectedOm && availableVcs.length > 0">
              <label for="vcSelect" class="block text-sm font-medium text-gray-700 mb-1">
                เลือก Value Chain (VC):
              </label>
              <select
                id="vcSelect"
                v-model="selectedVcId"
                @change="handleVcChange"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
               >
                 <option disabled value="">-- กรุณาเลือก VC --</option>
                 <option v-for="vc in availableVcs" :key="vc._id" :value="vc._id">
                  {{ vc.name }} {{ vc.subVcData && vc.subVcData.length > 0 ? '(มี Sub-VC)' : '' }}
                 </option>
              </select>
            </div>
            <div v-else-if="selectedOm && availableVcs.length === 0" class="text-sm text-gray-500 mt-2">
              * OM ที่เลือกไม่มีข้อมูล VC
            </div>
             <div v-else-if="!selectedOm" class="text-sm text-gray-500 mt-2">
              * กรุณาเลือก OM ก่อน
            </div>

            <!-- Sub-VC Selection (NEW) -->
            <div v-if="selectedVc && availableSubVcs.length > 0">
              <label for="subVcSelect" class="block text-sm font-medium text-gray-700 mb-1">
                เลือก Sub Value Chain (Sub-VC):
              </label>
              <select
                id="subVcSelect"
                v-model="selectedSubVcId"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option disabled value="">-- กรุณาเลือก Sub-VC --</option>
                <option v-for="subVc in availableSubVcs" :key="subVc._id" :value="subVc._id">
                  {{ subVc.name }}
                </option>
              </select>
            </div>
             <div v-else-if="selectedVc && (!selectedVc.subVcData || selectedVc.subVcData.length === 0)" class="text-sm text-gray-500 mt-2">
              * VC ที่เลือกไม่มีข้อมูล Sub-VC
            </div>
             <div v-else-if="selectedOm && availableVcs.length > 0 && !selectedVc" class="text-sm text-gray-500 mt-2">
              * กรุณาเลือก VC ก่อน (หากต้องการเลือก Sub-VC)
            </div>
          </div>
          
          <!-- Cluster Level Summary (shown when workingLevel is 'cluster') -->
          <div v-else-if="workingLevel === 'cluster'" class="bg-blue-50 p-4 rounded-lg">
            <div class="flex items-center">
              <i class="fas fa-sitemap text-blue-600 mr-3 text-xl"></i>
              <div>
                <h4 class="font-medium text-blue-900">การทำงานระดับ Cluster</h4>
                <p class="text-sm text-blue-700 mt-1">
                  จะแสดงข้อมูลและรายงานของ Cluster ทั้งหมด รวมทุก Operating Model และ Value Chain
                </p>
              </div>
            </div>
          </div>
          
          <!-- Selection Guide -->
          <div v-else class="text-center text-gray-500 text-sm">
            กรุณาเลือกระดับการทำงานที่ต้องการ
          </div>
        </div>
        <div class="items-center px-4 py-3 space-y-2">
          <button
            @click="confirmSelection"
            :disabled="!isSelectionComplete"
            class="px-4 py-2 bg-blue-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {{ workingLevel === 'cluster' ? 'ทำงานระดับ Cluster' : 'ยืนยันการเลือก OM/VC' }}
          </button>
           <button
             v-if="canCancel"
             @click="cancelSelection"
             class="px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
             ยกเลิก
           </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';

const props = defineProps({
  visible: Boolean,
  omData: {
    type: Array,
    required: true,
    default: () => []
  }
});

const emit = defineEmits(['confirm', 'cancel']);

const workingLevel = ref('cluster'); // 'cluster' or 'specific'
const selectedOmId = ref('');
const selectedVcId = ref('');
const selectedSubVcId = ref('');
const availableVcs = ref([]);
const availableSubVcs = ref([]);
const canCancel = ref(false);

const selectedOm = computed(() => {
  return Array.isArray(props.omData) ? props.omData.find(om => om._id === selectedOmId.value) : null;
});

const selectedVc = computed(() => {
    return availableVcs.value.find(vc => vc._id === selectedVcId.value);
});

const selectedSubVc = computed(() => {
    return availableSubVcs.value.find(subVc => subVc._id === selectedSubVcId.value);
});

const loadAndSetInitialSelection = async () => {
    let activeOmId = null;
    let activeVcId = null;
    let activeVcIsSubVc = false;

    canCancel.value = false;

    if (typeof window !== 'undefined' && window.localStorage) {
        try {
            const storedOmData = localStorage.getItem('activeOmData');
            const storedVcData = localStorage.getItem('activeVcData');

            if (storedOmData) {
                const parsedOm = JSON.parse(storedOmData);
                activeOmId = parsedOm?._id;
                canCancel.value = true;
            }
            if (storedVcData) {
                const parsedVc = JSON.parse(storedVcData);
                activeVcId = parsedVc?._id;
                activeVcIsSubVc = !!parsedVc?.vcId || !!parsedVc?.omId;
                canCancel.value = true;
            }

            selectedOmId.value = activeOmId || '';

            await nextTick();

            if (selectedOm.value && activeVcId) {
                const parentVc = availableVcs.value.find(vc =>
                    vc._id === activeVcId ||
                    (vc.subVcData && vc.subVcData.some(subVc => subVc._id === activeVcId))
                );

                if (parentVc) {
                    selectedVcId.value = parentVc._id;
                    await nextTick();

                    if (activeVcIsSubVc && availableSubVcs.value.some(subVc => subVc._id === activeVcId)) {
                         selectedSubVcId.value = activeVcId;
                    } else if (!activeVcIsSubVc && parentVc._id === activeVcId){
                        selectedSubVcId.value = '';
                    } else {
                        selectedSubVcId.value = '';
                    }
                } else {
                     selectedVcId.value = '';
                     selectedSubVcId.value = '';
                }
            } else {
                 selectedVcId.value = '';
                 selectedSubVcId.value = '';
            }

        } catch (e) {
            console.error("OMVCSelectionModal: Error parsing active data from localStorage:", e);
            selectedOmId.value = '';
            selectedVcId.value = '';
            selectedSubVcId.value = '';
            canCancel.value = false;
        }
    } else {
        console.warn("OMVCSelectionModal: localStorage not available.");
        canCancel.value = false;
    }
};

watch(() => props.visible, (newVal) => {
  if (newVal) {
    // Check if there's existing OM/VC selection in localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      const hasActiveOm = localStorage.getItem('activeOmData');
      const hasActiveVc = localStorage.getItem('activeVcData');
      
      // Set default working level based on existing selection
      if (hasActiveOm || hasActiveVc) {
        workingLevel.value = 'specific';
        loadAndSetInitialSelection();
      } else {
        workingLevel.value = 'cluster';
      }
    } else {
      workingLevel.value = 'cluster';
    }
  } else {
    // Reset all values when modal closes
    workingLevel.value = 'cluster';
    selectedOmId.value = '';
    selectedVcId.value = '';
    selectedSubVcId.value = '';
    availableVcs.value = [];
    availableSubVcs.value = [];
    canCancel.value = false;
  }
});

watch(selectedOm, (newOm) => {
  if (props.visible) {
      selectedVcId.value = '';
      selectedSubVcId.value = '';
  }
  availableVcs.value = [];
  availableSubVcs.value = [];

  if (newOm && Array.isArray(newOm.vcData)) {
    availableVcs.value = newOm.vcData;
  }
}, { immediate: false });

watch(selectedVc, (newVc) => {
    if (props.visible) {
       selectedSubVcId.value = '';
    }
    availableSubVcs.value = [];

    if (newVc && Array.isArray(newVc.subVcData) && newVc.subVcData.length > 0) {
        availableSubVcs.value = newVc.subVcData;
    }
}, { immediate: false });

// Watch for working level changes
watch(workingLevel, (newLevel) => {
    if (newLevel === 'cluster') {
        // Clear specific selections when switching to cluster level
        selectedOmId.value = '';
        selectedVcId.value = '';
        selectedSubVcId.value = '';
        availableVcs.value = [];
        availableSubVcs.value = [];
    } else if (newLevel === 'specific') {
        // Load existing selections when switching to specific level
        loadAndSetInitialSelection();
    }
});

const isSelectionComplete = computed(() => {
    // For cluster level, no specific selection needed
    if (workingLevel.value === 'cluster') {
        return true;
    }
    
    // For specific level, need OM selection
    if (workingLevel.value === 'specific') {
        if (!selectedOm.value) return false;

        if (availableVcs.value.length === 0) return true;

        if (!selectedVc.value) return false;

        if (availableSubVcs.value.length > 0 && !selectedSubVc.value) return false;

        return true;
    }
    
    return false;
});

const confirmSelection = () => {
  if (!isSelectionComplete.value) return;

  if (workingLevel.value === 'cluster') {
    // For cluster level, send null to indicate working at cluster level
    emit('confirm', {
      workingLevel: 'cluster',
      om: null,
      vc: null,
      message: 'ทำงานระดับ Cluster หลัก'
    });
  } else {
    // For specific level, send selected OM/VC data
    const finalVcData = selectedSubVc.value || selectedVc.value;
    
    emit('confirm', {
      workingLevel: 'specific',
      om: selectedOm.value,
      vc: finalVcData,
      message: `ทำงานใน ${selectedOm.value?.name}${finalVcData ? ` → ${finalVcData.name}` : ''}`
    });
  }
};

const cancelSelection = () => {
    emit('cancel');
};

const handleOmChange = () => { };
const handleVcChange = () => { };
</script>

<style scoped>
/* สามารถเพิ่ม CSS เพิ่มเติมสำหรับ Modal ได้ที่นี่ */
</style> 